import { handleApiError, handleApiResponse } from "@/methods/api";
import ApiVerified from "@/services/apiVerified";
import type { StudentPredesignedActivityWire } from "@/types/activity";
import type { AddBulkUsersResponse } from "@/types/admin";
import { ApiResponse, IdNamePair, PaginatedResponse } from "@/types/global";
import type {
  BulkAffiliationsResponse,
  ImportantDate,
  Olympiad,
  OlympiadAffiliationInputs,
  OlympiadB2CStudentInputs,
  OlympiadRegistrationTracking,
  RegisterAffiliationGradesInputs,
  RegisterAffiliationGradesResponse,
  RegisterMultipleAffiliationsInputs,
  RegisterMultipleBatchStudentsInputs,
  RegisterSingleAffiliationGradesInputs,
  RegisterSingleBatchStudentsInputs,
  RegisteredStudent,
  Syllabus,
} from "@/types/olympiad";

export default class OlympiadApi extends ApiVerified {
  async getOlympiadForStudent(userId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Olympiad>(`olympiad/get-olympiad-for-student`, {
        params: { userId: userId },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSyllabusForStudent(userId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Record<string, Array<Syllabus>>>(
        `olympiad/get-syllabus-for-student`,
        { params: { userId: userId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ─── Onboarding: direct (B2C) students ──────────────────────────────────────

  /** plan-§5.1 — POST olympiad/olympiad-b2c-students. Returns the generated login id. */
  async registerB2CStudent(inputs: OlympiadB2CStudentInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<string>(
        `olympiad/olympiad-b2c-students`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ─── Onboarding: schools and grades ─────────────────────────────────────────

  /** plan-§5.2 — POST olympiad/register-multiple-affiliations (step 1 of 2). */
  async registerMultipleAffiliations(
    inputs: RegisterMultipleAffiliationsInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<BulkAffiliationsResponse>(
        `olympiad/register-multiple-affiliations`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /**
   * POST olympiad/register-single-affiliation. Returns the generated affiliation login id.
   *
   * Replaces the public unverified-user/register-olympiad-affiliation, which never shipped. This
   * one is staff-only (JWTBearer), which is why it lives here rather than on unverifiedUserApi —
   * and why the required AffiliationManagerId is no longer a problem: an operator knows it, a
   * logged-out visitor never did.
   */
  async registerSingleAffiliation(inputs: OlympiadAffiliationInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<string>(
        `olympiad/register-single-affiliation`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /** plan-§5.3 — POST olympiad/register-affiliation-grades (step 2 of 2, bulk). */
  async registerAffiliationGrades(inputs: RegisterAffiliationGradesInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<RegisterAffiliationGradesResponse>(
        `olympiad/register-affiliation-grades`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /** plan-§5.4 — POST olympiad/register-single-affiliation-grades. Returns true. */
  async registerSingleAffiliationGrades(
    inputs: RegisterSingleAffiliationGradesInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `olympiad/register-single-affiliation-grades`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ─── Onboarding: bulk students ──────────────────────────────────────────────

  /** plan-§5.5 — POST olympiad/register-single-batch-olympiad-students. */
  async registerSingleBatchStudents(
    inputs: RegisterSingleBatchStudentsInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<AddBulkUsersResponse>(
        `olympiad/register-single-batch-olympiad-students`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /** plan-§5.6 — POST olympiad/register-multiple-batch-olympiad-students. */
  async registerMultipleBatchStudents(
    inputs: RegisterMultipleBatchStudentsInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<AddBulkUsersResponse>(
        `olympiad/register-multiple-batch-olympiad-students`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ─── Operations: tracking and support ───────────────────────────────────────

  /**
   * GET olympiad/all-olympiads — id/name pairs for the olympiad pickers (tracking, B2C register).
   * Resolves OQ-7: until this endpoint existed those screens had no way to list olympiads.
   */
  async getAllOlympiads(onlyActive: boolean = true): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(`olympiad/all-olympiads`, {
        params: { onlyActive: onlyActive },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /** plan-§5.7 — GET olympiad/{olympiadId}/registration-tracking. */
  /** GET olympiad/{olympiadId}/affiliations — schools registered in that olympiad, id/name. */
  async getOlympiadAffiliations(olympiadId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `olympiad/${olympiadId}/affiliations`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getRegistrationTracking(olympiadId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<OlympiadRegistrationTracking>(
        `olympiad/${olympiadId}/registration-tracking`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /** plan-§5.8 — POST olympiad/students/{userId}/resend-credentials. Sends no body. */
  async resendStudentCredentials(userId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `olympiad/students/${userId}/resend-credentials`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /**
   * GET olympiad/registered-students — one page of a batch roster. Paged because a direct-
   * registration batch can hold tens of thousands of students; count/pages ride on ApiResponse.
   * An empty search is not sent, so the backend sees the same request as "no search".
   */
  async getRegisteredStudents(
    batchId: number,
    pageNumber: number,
    pageSize: number,
    searchText: string = "",
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PaginatedResponse<RegisteredStudent>>(
        `olympiad/registered-students`,
        {
          params: {
            batchId: batchId,
            pageNumber: pageNumber,
            pageSize: pageSize,
            searchText: searchText.trim() || undefined,
          },
        },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ─── Student dashboard reads ────────────────────────────────────────────────

  /**
   * plan-§5.11 — GET olympiad/get-student-mock-tests.
   *
   * Returns the WIRE type, not the StudentPredesignedActivity view-model: the backend never
   * sends PredesignedActivityStatusId, which is stamped client-side from AttemptInfo.
   *
   * subjectId is optional and passed through params — ApiVerified's paramsSerializer drops
   * null/undefined, so an omitted subject sends no query param. Do not hand-build the string.
   */
  async getStudentMockTests(studentId: number, subjectId?: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<StudentPredesignedActivityWire>>(
        `olympiad/get-student-mock-tests`,
        { params: { studentId: studentId, subjectId: subjectId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /** plan-§5.12 — GET olympiad/students/{userId}/important-dates. Works for school-less B2C students. */
  async getStudentImportantDates(userId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<ImportantDate>>(
        `olympiad/students/${userId}/important-dates`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
}
